import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import AuthImage from "@/components/AuthImage";
import { ArrowLeft, ArrowRight, X, Check } from "lucide-react";
import { toast } from "sonner";

export default function ExploreDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [boat, setBoat] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  // Interest state (logged-in one-click vs guest form)
  const [interested, setInterested] = useState(false);
  const [interestBusy, setInterestBusy] = useState(false);

  // Interest modal state
  const [showInterest, setShowInterest] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    postal_code: "",
    comments: "",
  });

  useEffect(() => {
    api.get(`/explore/boats/${id}`)
      .then(({ data }) => setBoat(data))
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!user) { setInterested(false); return; }
    api.get("/interests/me")
      .then(({ data }) => setInterested((data.boat_ids || []).includes(id)))
      .catch(() => {});
  }, [user, id]);

  const toggleInterest = async () => {
    setInterestBusy(true);
    try {
      if (interested) {
        await api.delete(`/interests/${id}`);
        setInterested(false);
      } else {
        await api.post("/interests", { boat_id: id });
        setInterested(true);
        toast.success(t.explore.interestSavedTitle);
      }
    } catch {
      toast.error(t.landing.interest.error || "Error");
    } finally {
      setInterestBusy(false);
    }
  };

  useEffect(() => {
    if (user && showInterest && !sent) {
      setForm((f) => ({
        ...f,
        full_name: f.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
      }));
    }
  }, [user, showInterest, sent]);

  useEffect(() => {
    document.body.style.overflow = showInterest ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showInterest]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-slate-500">404</p>
        <Link to="/explorar" className="btn-outline mt-6 inline-flex text-sm" data-testid="explore-404-back">
          {t.explore.backToFleet}
        </Link>
      </div>
    );
  }

  if (!boat) return <div className="max-w-6xl mx-auto px-6 py-24 text-slate-400">·</div>;

  const gallery = boat.images || [];
  const primaryIdx = Math.max(0, gallery.findIndex((i) => i.is_primary));
  const displayed = gallery[active] || gallery[primaryIdx] || null;
  const ev = Number(boat.estimated_value || 0);
  const share = Number(boat.share_price || 0);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitInterest = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/interest-requests", { ...form, boat_id: id });
      setSent(true);
      toast.success(t.landing.interest.sentTitle);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const closeModal = () => {
    setShowInterest(false);
    setTimeout(() => {
      setSent(false);
      setErr("");
    }, 300);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <Link to="/explorar" className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="explore-detail-back">
        <ArrowLeft className="h-4 w-4" /> {t.explore.backToFleet}
      </Link>

      <div className="mt-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="aspect-[16/10] rounded-[20px] overflow-hidden bg-slate-100" data-testid="explore-detail-main">
            {displayed ? (
              <AuthImage path={displayed.storage_path} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-slate-400">— no photos —</div>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-3">
              {gallery.map((img, i) => (
                <button
                  key={img.id}
                  onClick={() => setActive(i)}
                  className={`aspect-[4/3] rounded-lg overflow-hidden border-2 transition-colors ${i === active ? "border-[#0B192C]" : "border-transparent"}`}
                  data-testid={`explore-thumb-${i}`}
                >
                  <AuthImage path={img.storage_path} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-4">
          <p className="eyebrow">{boat.type ? t.boats.types[boat.type] : ""}</p>
          <h1 className="font-serif text-3xl lg:text-4xl mt-2 text-[#0B192C]" data-testid="explore-detail-title">
            {boat.brand} {boat.model}
          </h1>
          <p className="mt-1 text-slate-500 text-sm">
            {boat.year} · {boat.location}{boat.port ? ` · ${boat.port}` : ""}
          </p>

          <div className="mt-8 rounded-2xl border border-slate-100 bg-[#F8F9FA] p-6">
            <p className="eyebrow">{t.boats.section.ownership}</p>
            <p className="mt-1 font-serif text-xl text-[#0B192C]">{t.boats.ownership.standardDesc}</p>
            <div className="mt-5 flex items-center gap-1 h-2">
              {[0, 1, 2, 3].map((i) => <div key={i} className="flex-1 rounded-full bg-[#C2A878]/90 h-full" />)}
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] font-display uppercase tracking-widest text-slate-400">{t.boats.ownership.boatValue}</p>
                <p className="mt-1 font-serif text-lg text-[#0B192C]" data-testid="explore-detail-value">€{ev.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] font-display uppercase tracking-widest text-slate-400">{t.boats.ownership.sharePrice}</p>
                <p className="mt-1 font-serif text-lg text-[#0B192C]" data-testid="explore-detail-share-price">€{share.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] font-display uppercase tracking-widest text-slate-400">{t.boats.ownership.owners}</p>
                <p className="mt-1 font-serif text-lg text-slate-700">4</p>
              </div>
              <div>
                <p className="text-[10px] font-display uppercase tracking-widest text-slate-400">{t.boats.ownership.percentage}</p>
                <p className="mt-1 font-serif text-lg text-slate-700">25%</p>
              </div>
            </div>
            {user ? (
              <>
                <button
                  onClick={toggleInterest}
                  disabled={interestBusy}
                  className={`w-full justify-center mt-6 disabled:opacity-60 ${interested ? "btn-outline" : "btn-primary"}`}
                  data-testid="explore-interested"
                >
                  {interested ? (
                    <><Check className="h-4 w-4" /> {t.explore.interestedActive}</>
                  ) : (
                    <>{t.explore.interested} <ArrowRight className="h-4 w-4" /></>
                  )}
                </button>
                {interested && (
                  <>
                    <p className="mt-3 text-xs text-slate-500 text-center leading-relaxed" data-testid="explore-interest-hint">
                      {t.explore.interestSavedBody}
                    </p>
                    <button
                      onClick={toggleInterest}
                      disabled={interestBusy}
                      className="mt-2 w-full text-center text-xs text-slate-400 hover:text-slate-600 underline underline-offset-4"
                      data-testid="explore-remove-interest"
                    >
                      {t.explore.removeInterest}
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowInterest(true)}
                  className="btn-primary w-full justify-center mt-6"
                  data-testid="explore-interested"
                >
                  {t.explore.interested} <ArrowRight className="h-4 w-4" />
                </button>
                <p className="mt-3 text-xs text-slate-400 text-center">{t.explore.loginToContinue}</p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-16 grid lg:grid-cols-2 gap-10">
        <section>
          <h2 className="font-display text-xs tracking-[0.24em] uppercase text-slate-400 mb-4">{t.boats.section.basic}</h2>
          <dl className="divide-y divide-slate-100 border-t border-slate-100" data-testid="explore-detail-specs">
            {[
              [t.boats.f.type, t.boats.types[boat.type]],
              [t.boats.f.length, boat.length ? `${boat.length} m` : "—"],
              [t.boats.f.beam, boat.beam ? `${boat.beam} m` : "—"],
              [t.boats.f.capacity, boat.capacity || "—"],
              [t.boats.f.engine, boat.engine || "—"],
              [t.boats.f.year, boat.year || "—"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-3 text-sm">
                <dt className="text-slate-500">{k}</dt>
                <dd className="font-display text-[#0B192C]">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section>
          <h2 className="font-display text-xs tracking-[0.24em] uppercase text-slate-400 mb-4">{t.boats.section.economic}</h2>
          <dl className="divide-y divide-slate-100 border-t border-slate-100" data-testid="explore-detail-economic">
            <div className="flex items-center justify-between py-3 text-sm">
              <dt className="text-slate-500">{t.boats.f.estimatedValue}</dt>
              <dd className="font-display text-[#0B192C]">€{ev.toLocaleString()}</dd>
            </div>
            <div className="flex items-center justify-between py-3 text-sm">
              <dt className="text-slate-500">{t.boats.f.annualCost}</dt>
              <dd className="font-display text-[#0B192C]">€{Number(boat.annual_cost || 0).toLocaleString()}</dd>
            </div>
            <div className="flex items-center justify-between py-3 text-sm">
              <dt className="text-slate-500">{t.boats.f.mooringCost}</dt>
              <dd className="font-display text-[#0B192C]">€{Number(boat.mooring_cost || 0).toLocaleString()}</dd>
            </div>
          </dl>
        </section>
      </div>

      {boat.description && (
        <section className="mt-14">
          <h2 className="font-display text-xs tracking-[0.24em] uppercase text-slate-400 mb-4">{t.boats.section.condition}</h2>
          <div className="rounded-2xl border border-slate-100 p-6">
            <p className="text-slate-600 leading-relaxed">{boat.description}</p>
          </div>
        </section>
      )}

      {/* ---------------- INTEREST MODAL ---------------- */}
      {showInterest && (
        <div
          className="fixed inset-0 z-50 bg-[#0B192C]/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="interest-modal"
        >
          <div
            className="relative bg-white rounded-[20px] w-full max-w-lg max-h-[92vh] overflow-y-auto p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full hover:bg-slate-100 transition-colors"
              data-testid="interest-close"
            >
              <X className="h-4 w-4" />
            </button>

            {!sent ? (
              <>
                <p className="eyebrow">{boat.brand} {boat.model}</p>
                <h2 className="font-serif text-2xl sm:text-3xl mt-2 text-[#0B192C] leading-tight" data-testid="interest-title">
                  {t.landing.interest.title}
                </h2>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  {t.landing.interest.subtitle}
                </p>

                <form onSubmit={submitInterest} className="mt-6 space-y-4" data-testid="interest-form">
                  <div>
                    <label className="label-elegant">{t.landing.interest.fullName}</label>
                    <input
                      required
                      className="input-elegant"
                      value={form.full_name}
                      onChange={setField("full_name")}
                      data-testid="interest-full-name"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label-elegant">{t.landing.interest.email}</label>
                      <input
                        required
                        type="email"
                        className="input-elegant"
                        value={form.email}
                        onChange={setField("email")}
                        data-testid="interest-email"
                      />
                    </div>
                    <div>
                      <label className="label-elegant">{t.landing.interest.phone}</label>
                      <input
                        className="input-elegant"
                        value={form.phone}
                        onChange={setField("phone")}
                        data-testid="interest-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label-elegant">{t.landing.interest.postalCode}</label>
                    <input
                      className="input-elegant"
                      value={form.postal_code}
                      onChange={setField("postal_code")}
                      data-testid="interest-postal-code"
                    />
                  </div>
                  <div>
                    <label className="label-elegant">{t.landing.interest.comments}</label>
                    <textarea
                      rows={3}
                      className="input-elegant resize-none"
                      value={form.comments}
                      onChange={setField("comments")}
                      data-testid="interest-comments"
                    />
                  </div>
                  {err && <p className="text-sm text-red-600" data-testid="interest-error">{err}</p>}
                  <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3">
                    <button
                      type="submit"
                      disabled={busy}
                      className="btn-primary flex-1 justify-center disabled:opacity-60"
                      data-testid="interest-submit"
                    >
                      {t.landing.interest.send} <ArrowRight className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="btn-ghost text-sm"
                      data-testid="interest-cancel"
                    >
                      {t.landing.interest.cancel}
                    </button>
                  </div>
                  <p className="pt-4 text-center text-xs text-slate-500">
                    {t.landing.interest.or}{" "}
                    <Link to="/registro" className="text-[#0B192C] font-medium underline underline-offset-4" data-testid="interest-register-link">
                      {t.landing.interest.registerInstead}
                    </Link>
                  </p>
                </form>
              </>
            ) : (
              <div className="py-8 text-center" data-testid="interest-sent">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#C2A878]/15 grid place-items-center">
                  <Check className="h-6 w-6 text-[#C2A878]" strokeWidth={2} />
                </div>
                <h3 className="font-serif text-2xl mt-6 text-[#0B192C]">
                  {t.landing.interest.sentTitle}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  {t.landing.interest.sentBody}
                </p>
                <button onClick={closeModal} className="btn-primary mt-8" data-testid="interest-sent-close">
                  {t.landing.interest.close}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
